import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import PageShell from "../components/PageShell";
import PageHero from "../components/PageHero";
import { POSTS } from "../data/posts";

const TAGS = ["All", "AI", "Engineering", "Security", "Product", "Studio"];

function formatDate(iso) {
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export default function BlogPage() {
  const [tag, setTag] = React.useState("All");
  const filtered = POSTS.filter((p) => tag === "All" || p.tag === tag);
  const [featured, ...rest] = filtered;

  return (
    <PageShell>
      <PageHero
        eyebrow="◇ Writing"
        title="Notes from the studio."
        accent="studio."
        kicker="Field notes on engineering, AI systems, and what it actually takes to ship software that has to work."
      />

      {/* Tag bar */}
      <div className="sticky top-16 md:top-20 z-30 bg-canvas/80 backdrop-blur-2xl border-b border-hairline">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex items-center gap-2 overflow-x-auto">
          <div className="text-[11px] tracking-[0.25em] uppercase text-muted-x font-mono-x mr-3 shrink-0">
            Filter
          </div>
          {TAGS.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTag(t)}
              data-testid={`blog-filter-${t.toLowerCase()}`}
              className={`shrink-0 text-xs px-3 py-1.5 rounded-full border transition-colors ${
                tag === t
                  ? "border-transparent"
                  : "border-hairline-strong text-secondary-x hover:text-primary-x"
              }`}
              style={
                tag === t
                  ? { background: "var(--text-primary)", color: "var(--canvas)" }
                  : {}
              }
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      {/* Featured */}
      {featured && (
        <section className="py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <Link
              to={`/blog/${featured.slug}`}
              data-testid={`blog-featured-${featured.slug}`}
              className="group grid md:grid-cols-12 gap-8 md:gap-12 items-center"
            >
              <div className="md:col-span-7 relative aspect-[16/10] overflow-hidden rounded-2xl border border-hairline bg-surface-2">
                <motion.img
                  initial={{ scale: 1.06, opacity: 0 }}
                  whileInView={{ scale: 1, opacity: 0.85 }}
                  viewport={{ once: true }}
                  transition={{ duration: 1.1 }}
                  src={featured.cover}
                  alt={featured.title}
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
              </div>
              <div className="md:col-span-5">
                <div className="text-[11px] tracking-[0.25em] uppercase text-muted-x font-mono-x mb-4 flex items-center gap-3">
                  <span>{featured.tag}</span>
                  <span className="h-px w-8 bg-hairline-strong" />
                  <span>{formatDate(featured.date)}</span>
                  <span className="h-px w-8 bg-hairline-strong" />
                  <span>{featured.readTime}</span>
                </div>
                <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.05] text-primary-x text-balance">
                  {featured.title}
                </h2>
                <p className="mt-6 text-secondary-x leading-relaxed text-lg">
                  {featured.excerpt}
                </p>
                <div className="mt-8 flex items-center gap-3 text-sm">
                  <span className="text-primary-x">{featured.author}</span>
                  <span className="text-muted-x">·</span>
                  <span className="text-muted-x">{featured.role}</span>
                </div>
                <div className="mt-8 inline-flex items-center gap-2 text-primary-x">
                  Read article
                  <span className="transition-transform group-hover:translate-x-1">→</span>
                </div>
              </div>
            </Link>
          </div>
        </section>
      )}

      {/* Grid */}
      <section className="pb-28 md:pb-40 border-t border-hairline">
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-16">
          {rest.length === 0 ? (
            <div className="text-secondary-x text-center py-20">
              No posts in this filter — yet.
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
              {rest.map((p, i) => (
                <motion.article
                  key={p.slug}
                  initial={{ y: 30, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ delay: i * 0.06, duration: 0.7 }}
                >
                  <Link
                    to={`/blog/${p.slug}`}
                    data-testid={`blog-card-${p.slug}`}
                    className="group block"
                  >
                    <div className="relative aspect-[5/4] overflow-hidden rounded-xl border border-hairline bg-surface-2 mb-6">
                      <img
                        src={p.cover}
                        alt={p.title}
                        loading="lazy"
                        className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"
                      />
                    </div>
                    <div className="text-[11px] tracking-[0.25em] uppercase text-muted-x font-mono-x mb-3 flex items-center gap-3">
                      <span>{p.tag}</span>
                      <span className="h-px w-6 bg-hairline-strong" />
                      <span>{formatDate(p.date)}</span>
                    </div>
                    <h3 className="font-display text-2xl md:text-3xl font-medium tracking-tight text-primary-x leading-tight group-hover:opacity-90 transition-opacity">
                      {p.title}
                    </h3>
                    <p className="mt-4 text-secondary-x leading-relaxed line-clamp-3">
                      {p.excerpt}
                    </p>
                    <div className="mt-6 flex items-center gap-3 text-xs">
                      <span className="text-primary-x">{p.author}</span>
                      <span className="text-muted-x">·</span>
                      <span className="text-muted-x">{p.readTime}</span>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>
    </PageShell>
  );
}
